import React, { useEffect, useState } from 'react';
import { adminsApi, eventsApi } from '../services/api';

const EMPTY = { name: '', email: '', password: '', role: 'organizer' };

export default function OrganizersPage() {
  const [organizers, setOrganizers] = useState([]);
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [assigning, setAssigning] = useState(null); // organizer being assigned events
  const [loading, setLoading] = useState(false);

  const load = () => {
    adminsApi.list().then(r => setOrganizers(r.data)).catch(() => {});
    eventsApi.listPublic().then(r => setEvents(r.data)).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editing) {
        const data = { name: form.name, email: form.email, role: form.role };
        if (form.password) data.password = form.password;
        await adminsApi.update(editing, data);
      } else {
        await adminsApi.create(form);
      }
      setShowForm(false); setEditing(null); setForm(EMPTY);
      load();
    } catch (err) {
      alert('Errore: ' + (err.response?.data?.error || err.message));
    } finally { setLoading(false); }
  };

  const toggleActive = async (org) => {
    await adminsApi.update(org.id, { is_active: !org.is_active });
    load();
  };

  const assignEvent = async (orgId, eventId, assigned) => {
    if (assigned) {
      await adminsApi.removeEvent(orgId, eventId);
    } else {
      await adminsApi.assignEvent(orgId, eventId);
    }
    load();
  };

  const f = (key) => (e) => setForm(p => ({ ...p, [key]: e.target.value }));

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>👤 Organizzatori</h2>
          <p style={styles.subtitle}>Gestisci gli organizzatori e assegna gli eventi</p>
        </div>
        <button style={styles.btnPrimary} onClick={() => { setForm(EMPTY); setEditing(null); setShowForm(true); }}>
          + Nuovo organizzatore
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <form onSubmit={save} style={styles.formCard}>
          <h3 style={styles.formTitle}>{editing ? '✏️ Modifica organizzatore' : '➕ Nuovo organizzatore'}</h3>
          <div style={styles.grid3}>
            <label style={styles.label}>Nome completo *
              <input style={styles.input} value={form.name} onChange={f('name')} required placeholder="Mario Rossi" />
            </label>
            <label style={styles.label}>Email *
              <input style={styles.input} type="email" value={form.email} onChange={f('email')} required placeholder="mario@esempio.it" />
            </label>
            <label style={styles.label}>{editing ? 'Nuova password (opzionale)' : 'Password *'}
              <input style={styles.input} type="password" value={form.password} onChange={f('password')} required={!editing} placeholder="••••••••" />
            </label>
            <label style={styles.label}>Ruolo
              <select style={styles.input} value={form.role} onChange={f('role')}>
                <option value="organizer">Organizzatore</option>
                <option value="superadmin">Superadmin</option>
              </select>
            </label>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <button style={styles.btnPrimary} type="submit" disabled={loading}>
              {loading ? 'Salvataggio...' : '💾 Salva'}
            </button>
            <button style={styles.btnSecondary} type="button" onClick={() => setShowForm(false)}>Annulla</button>
          </div>
        </form>
      )}

      {/* Assegna eventi */}
      {assigning && (
        <div style={styles.assignCard}>
          <div style={styles.assignHeader}>
            <h3 style={styles.formTitle}>
              📅 Assegna eventi a <span style={{ color: '#F26522' }}>{assigning.name}</span>
            </h3>
            <button style={styles.btnSecondary} onClick={() => setAssigning(null)}>Chiudi</button>
          </div>
          <p style={styles.assignHint}>Seleziona gli eventi che questo organizzatore potrà modificare:</p>
          <div style={styles.eventList}>
            {events.map(ev => {
              const assigned = ev.organizer_id === assigning.id;
              return (
                <div key={ev.id} style={[styles.eventRow, assigned ? styles.eventRowAssigned : {}]}>
                  <div style={{ flex: 1 }}>
                    <div style={styles.eventName}>{ev.name}</div>
                    <div style={styles.eventMeta}>📅 {ev.date} · 📍 {ev.city}</div>
                  </div>
                  <button
                    style={assigned ? styles.btnRemove : styles.btnAssign}
                    onClick={() => assignEvent(assigning.id, ev.id, assigned).then(load)}
                  >
                    {assigned ? '✓ Assegnato' : '+ Assegna'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Table */}
      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              {['Nome', 'Email', 'Ruolo', 'Stato', 'Eventi assegnati', 'Azioni'].map(h => (
                <th key={h} style={styles.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {organizers.map(org => {
              const assignedCount = events.filter(e => e.organizer_id === org.id).length;
              return (
                <tr key={org.id} style={{ opacity: org.is_active ? 1 : 0.5 }}>
                  <td style={styles.td}>
                    <div style={styles.orgName}>
                      <div style={styles.orgAvatar}>{org.name[0]?.toUpperCase()}</div>
                      {org.name}
                    </div>
                  </td>
                  <td style={styles.td}>{org.email}</td>
                  <td style={styles.td}>
                    <span style={org.role === 'superadmin' ? styles.badgeSuperadmin : styles.badgeOrg}>
                      {org.role === 'superadmin' ? '⭐ Superadmin' : '👤 Organizzatore'}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <span style={org.is_active ? styles.badgeActive : styles.badgeInactive}>
                      {org.is_active ? '● Attivo' : '○ Inattivo'}
                    </span>
                  </td>
                  <td style={styles.td}>
                    {org.role !== 'superadmin' ? (
                      <span style={styles.countBadge}>{assignedCount} eventi</span>
                    ) : '—'}
                  </td>
                  <td style={styles.td}>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button style={styles.btnSmall} onClick={() => {
                        setForm({ name: org.name, email: org.email, password: '', role: org.role });
                        setEditing(org.id); setShowForm(true);
                      }}>✏️</button>
                      {org.role !== 'superadmin' && (
                        <button style={styles.btnSmall} onClick={() => setAssigning(org)}>📅</button>
                      )}
                      <button
                        style={{ ...styles.btnSmall, color: org.is_active ? '#e74c3c' : '#27ae60' }}
                        onClick={() => toggleActive(org)}
                      >
                        {org.is_active ? '🔒' : '🔓'}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {organizers.length === 0 && (
              <tr><td colSpan={6} style={{ ...styles.td, textAlign: 'center', color: '#aaa', padding: 30 }}>
                Nessun organizzatore ancora. Creane uno!
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const styles = {
  page:     { padding: 24 },
  header:   { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
  title:    { margin: 0, color: '#0D3B6E', fontSize: 22 },
  subtitle: { margin: '4px 0 0', color: '#888', fontSize: 13 },

  formCard:  { background: '#fff', borderRadius: 14, padding: 24, marginBottom: 20, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' },
  formTitle: { margin: '0 0 16px', color: '#0D3B6E', fontSize: 17 },
  grid3:     { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14 },
  label:     { display: 'block', fontSize: 12, fontWeight: 700, color: '#555', letterSpacing: 0.3 },
  input: {
    display: 'block', width: '100%', padding: '9px 11px',
    borderRadius: 8, border: '1.5px solid #ddd', marginTop: 5,
    fontSize: 14, boxSizing: 'border-box', color: '#333',
  },

  // Assign card
  assignCard:   { background: '#fff', borderRadius: 14, padding: 20, marginBottom: 20, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: '2px solid #F26522' },
  assignHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
  assignHint:   { color: '#888', fontSize: 13, marginBottom: 14 },
  eventList:    { display: 'flex', flexDirection: 'column', gap: 8 },
  eventRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '10px 14px', borderRadius: 10, border: '1px solid #eee', background: '#fafafa',
  },
  eventRowAssigned: { background: '#F0FFF4', border: '1px solid #a8e6c1' },
  eventName:    { fontSize: 14, fontWeight: 600, color: '#0D3B6E' },
  eventMeta:    { fontSize: 12, color: '#888', marginTop: 2 },

  // Table
  tableWrap: { background: '#fff', borderRadius: 14, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.07)' },
  table:     { width: '100%', borderCollapse: 'collapse' },
  th:        { background: '#0D3B6E', color: '#fff', padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, letterSpacing: 0.5 },
  td:        { padding: '12px 16px', borderBottom: '1px solid #f0f0f0', fontSize: 13 },

  orgName:   { display: 'flex', alignItems: 'center', gap: 10 },
  orgAvatar: {
    width: 30, height: 30, borderRadius: 15, background: '#F26522',
    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: 900, fontSize: 13, flexShrink: 0,
  },

  // Badges
  badgeSuperadmin: { background: '#FFF8E1', color: '#b45309', borderRadius: 6, padding: '3px 8px', fontSize: 12, fontWeight: 700 },
  badgeOrg:        { background: '#EEF2FF', color: '#4C6FFF', borderRadius: 6, padding: '3px 8px', fontSize: 12, fontWeight: 700 },
  badgeActive:     { color: '#27ae60', fontSize: 12, fontWeight: 700 },
  badgeInactive:   { color: '#aaa', fontSize: 12 },
  countBadge:      { background: '#F0F4FF', color: '#4C6FFF', borderRadius: 6, padding: '3px 8px', fontSize: 12, fontWeight: 700 },

  // Buttons
  btnPrimary:   { padding: '9px 18px', background: '#F26522', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 14 },
  btnSecondary: { padding: '9px 18px', background: '#eee', color: '#333', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14 },
  btnSmall:     { padding: '5px 11px', background: '#f0f2f5', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 },
  btnAssign:    { padding: '6px 14px', background: '#0D3B6E', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700 },
  btnRemove:    { padding: '6px 14px', background: '#F0FFF4', color: '#27ae60', border: '1px solid #a8e6c1', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700 },
};
